import { apiClient } from './client';
import type {
  RegionsResponse,
  RegionResponse,
  RegionServicesResponse,
  RegionFilters,
  CreateRegionData,
  UpdateRegionData,
  NearestRegionRequest,
  NearestRegionResponse,
  RegionSearchRequest,
  RegionSearchResponse,
} from '@/types';

/**
 * Regions API Client
 * Provides methods for interacting with region endpoints
 */
export const regionsAPI = {
  /**
   * Get all regions with optional filtering
   */
  getAll: (params?: RegionFilters) =>
    apiClient.get<RegionsResponse>('/regions', { params }),

  /**
   * Get region by ID with available services
   */
  getById: (id: number) =>
    apiClient.get<RegionResponse>(`/regions/${id}`),

  /**
   * Get all services available in a region
   */
  getServices: (regionId: number, params?: { page?: number; limit?: number; serviceType?: string; isAvailable?: boolean }) =>
    apiClient.get<RegionServicesResponse>(`/regions/${regionId}/services`, { params }),

  /**
   * Get regions by governorate
   */
  getByGovernorate: (governorate: string) =>
    apiClient.get<RegionsResponse>(`/regions/governorate/${encodeURIComponent(governorate)}`),

  /**
   * Create a new region (Admin only)
   */
  create: (data: CreateRegionData) =>
    apiClient.post<RegionResponse>('/regions', data),

  /**
   * Update an existing region (Admin only)
   */
  update: (id: number, data: UpdateRegionData) =>
    apiClient.put<RegionResponse>(`/regions/${id}`, data),

  /**
   * Delete a region (Admin only)
   */
  delete: (id: number) =>
    apiClient.delete(`/regions/${id}`),

  /**
   * Find the nearest region based on various input types
   * Supports: Arabic/English name, coordinates, or Google Maps URL
   */
  findNearest: (data: NearestRegionRequest) =>
    apiClient.post<NearestRegionResponse>('/regions/nearest', data),

  /**
   * Search regions with multiple criteria
   * Returns multiple matching regions
   */
  search: (data: RegionSearchRequest) =>
    apiClient.post<RegionSearchResponse>('/regions/search', data),
};
